import type { AppAction } from './types/ActionTypes'
import {
  API_CALL,
  API_CALL_SUCCESS,
  CLEAR_FLIGHT_RESULT,
  CLEAR_FLIGHT_SEARCH_CACHE,
  FETCH_AIRLINES,
  FETCH_FLIGHT_CAMPAIGN,
  FETCH_FLIGHT_CAMPAIGN_DEALS,
  FETCH_FLIGHT_CAMPAIGNS,
  FETCH_FLIGHT_DEAL,
  FETCH_FLIGHT_DEALS,
  FETCH_POPULAR_AIRPORTS,
  SET_FLIGHTS_CREDIT_DATA,
  UPDATE_FLIGHT_FILTERS,
  UPDATE_FLIGHT_TRAVELLERS,
  UPDATE_FLIGHTS_SORT,
  UPDATE_FLIGHT_RESERVATION_DETAILS,
  UPDATE_FLIGHT_SEAT_SELECTIONS,
  CHECKOUT_UPDATE_FLIGHT_SEAT_SELECTIONS,
  CLEAR_FLIGHT_SEAT_SELECTIONS,
  CHECKOUT_CLEAR_FLIGHT_SEAT_SELECTIONS,
  CHECKOUT_UPDATE_ECONOMY_LUX_COUPLES_CONFIGURATION,
  CHECKOUT_CLEAR_ECONOMY_LUX_COUPLES_CONFIGURATION,
  FLIGHTS_DATE_CHANGE_SET_STEP,
  FLIGHTS_DATE_CHANGE_SELECTED_LEGS_TO_CHANGE,
  FLIGHTS_DATE_CHANGE_NEW_LEGS_WITH_DATES,
  FLIGHTS_DATE_CHANGE_RESET,
  CHECKOUT_RESET_FLIGHT_ITEMS,
} from './actionConstants'
import { getCheckoutFlightItemFinalFare } from './CheckoutFlightActions'
import type {
  FlightSearchItinerary,
  FlightSeatTraveler,

  FlightDateGridRequest,
} from 'api/flights'
import {
  getAirlines,
  getFlightDealsByRegion,
  getFlightPrice,
  getJourneyV2,
  getPopularAirportsByRegion,
  searchFlights,
  getFlightCachePrices,
  getFlightFareFamilies,
  getFlightDeal,
  getFlightCampaigns,
  getFlightCampaignDeals,
  getFlightCampaign,
  updateFlightReservationTravellers,
  updateFlightReservationDetails,
  getRecentFlightSearches,
  getFlightsDateChangeFlightsList,
  getDateChangeJourneyV2,
  getFlightSeats,
  getFlightPenaltiesDetails,
  getFlightDateGrid,
  createFareWatch as createFareWatchRequest,
  fetchFareWatches as fetchFareWatchesRequest,
  deleteFareWatch as deleteFareWatchRequest,
  fetchFareWatchStatus as fetchFareWatchStatusRequest,
  getFareSenseSuggestions,
} from 'api/flights'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { getFlightItems } from 'checkout/selectors/view/flights'
import type { FlightPriceKey } from 'lib/flights/flightUtils'
import { FLIGHT_DATE_GRID_ALTERNATIVE_DATES_COUNT, FLIGHT_DATE_GRID_ALTERNATIVE_DATES_COUNT_NDC, getFlightCalendarKey, resetFilter, getFlightPriceKey, getFlightSearchKey, getDateChangeJourneyV2IdKey, getFlightDateGridKey, getFareWatchStatusKey } from 'lib/flights/flightUtils'
import {
  FETCH_FLIGHT_JOURNEY,
  FETCH_FLIGHT_PRICE,
  FETCH_FLIGHT_CALENDAR,
  FETCH_FLIGHT_DATE_GRID,
  FETCH_SEARCH_FLIGHTS,
  FETCH_FLIGHT_FARE_FAMILIES,
  FETCH_RECENT_FLIGHT_SEARCHES,
  FETCH_FLIGHTS_DATE_CHANGE_FLIGHTS_LIST,
  FETCH_FLIGHT_SEATS,
  FETCH_FLIGHT_PENALTIES_DETAILS,
  FETCH_FARE_WATCHES,
  DELETE_FARE_WATCH,
  FETCH_FARE_WATCH_STATUS,
  FETCH_FARESENSE_SUGGESTIONS,
} from './apiActionConstants'
import { sum } from 'lib/array/arrayUtils'
import type { FlightsClassTypes, FlightSortOptions } from 'constants/flight'
import type { FlightsCreditDetails } from 'components/Flights/types'
import type { CreateFareWatchRequest, FareWatchStatusQuery } from 'components/Flights/PriceAlerts/types'
import { resetFlightsTimer } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import getObjectKey from 'lib/object/getObjectKey'
import config from 'constants/config'
import { EmptyObject } from 'lib/object/objectUtils'
import type { FlightChangeDatesPageStepDefinition } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/flights'
import type { SeatSelections } from 'components/Flights/SeatsSelection/types'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { getCanViewLuxPlusBenefits, getIsEnabledFlightsBundledLuxPlusPricing, getIsEnabledFlightsLuxPlusPricing } from 'luxPlus/selectors/featureToggle'
import { isLuxPlusSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscription'

interface GetFlightsParams {
  itinerary: Array<FlightSearchItinerary>
  currency: string
  occupancies: Array<App.Occupants>
  fareClass: string
  maxArrivalTime?: string
  minReturningDepartureTime?: string
  carriers?: Array<string>
  fareType?: string
  source?: string
  searchIndex?: number
  viewType?: string
  forceBundleId?: string
  filters?: {
    filterArilines?: Array<string>
  }
  accommodationCheckInDate?: string
  email?: string
  feederOriginAirportCode?: string
  /** Forwards `isFeederBundle: true` to /flightsearch (FLY-5300). Set on feeder leg search only. */
  isFeederBundle?: boolean
}

export function fetchAirlines(code: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.airlines[code]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_AIRLINES,
      request: () => getAirlines(code),
    })
  }
}

export function fetchFlightJourneyV2(
  searchId: string,
  journeyId: string,
  fareFamilyId?: string,
  /**
   * Forwarded to GET /flightSearch/{searchId}/{fareId} so svc-flights can apply the charter
   * feeder markup dynamically. Absent (e.g. customer skipped connecting flights) →
   * MEL self-feeder markup is applied. Source via `getFeederOriginAirportCode(state)`.
   */
  feederOriginAirportCode?: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const journeyKey = getObjectKey({
      journeyId,
      searchId,
      fareFamilyId,
    })
    const cachedFeederOriginAirportCode = state.flights.journeyFeederOriginAirportCodeByKey?.[journeyKey]
    const hasMatchingFeederOriginContext = cachedFeederOriginAirportCode === feederOriginAirportCode

    if (
      hasMatchingFeederOriginContext &&
      (state.flights.journeysById[journeyKey] || state.flights.journeyFetching[journeyKey])
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_JOURNEY,
      key: journeyKey,
      feederOriginAirportCode,
      request: () => getJourneyV2(searchId, journeyId, state.auth.accessToken!, fareFamilyId, feederOriginAirportCode),
    })
  }
}

export function fetchDateChangeJourneyV2(
  reservationId: string,
  searchId: string,
  journeyId: string,
  selectedUpsellOptionId?: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const journeyKey = getDateChangeJourneyV2IdKey(reservationId, searchId, journeyId)

    if (state.flights.journeysById[journeyKey] || state.flights.journeyFetching[journeyKey]) {
      return
    }

    dispatch({
      type: API_CALL,
      // Using the same action type as the regular journey fetch to avoid duplication. Its the same structure and is handled in the same way.
      api: FETCH_FLIGHT_JOURNEY,
      key: journeyKey,
      request: () => getDateChangeJourneyV2(reservationId, searchId, journeyId, selectedUpsellOptionId),
    })
  }
}

export function fetchFlights(
  params: GetFlightsParams,
  options: {
    offerId?: string
    offerTilePrice?: number
    offerCalendarPrice?: number
    enableSabreContent?: boolean
    enableLuxPlusPricing?: boolean
    isBundledFlow?: boolean
  } = EmptyObject,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getFlightSearchKey(params)
    const { enableLuxPlusPricing, isBundledFlow, ...searchOptions } = options
    const canRequestLuxPlusPricing = getCanViewLuxPlusBenefits(state) || (!!isBundledFlow && isLuxPlusSubscriptionInCart(state))
    const requestLuxPlusPricing = !!(enableLuxPlusPricing && canRequestLuxPlusPricing)

    if (state.flights.searchFlights[key] || state.flights.searchV2Flights[key]) {
      // already fetched or is fetching, don't fetch again
      return
    }

    resetFlightsTimer()

    // TODO: Look into adding an expiry time for the results
    // so they don't stick around forever and go out of date

    dispatch({
      type: API_CALL,
      api: FETCH_SEARCH_FLIGHTS,
      request: () => searchFlights(
        {
          ...params,
          region: state.geo.currentRegionCode,
          ...(isAgentHubEnabled && { excludeProviders: ['sabre', 'jetstar'] }),
          email: state.auth.account.email,
        },
        {
          ...searchOptions,
          requestLuxPlusPricing,
        },
      ),
      key,
    })
  }
}

export function updateFlightFilters<T extends App.AnyJourney = App.Journey>(filters: Array<App.FlightFilter<T>>) {
  return {
    type: UPDATE_FLIGHT_FILTERS,
    data: filters,
  }
}

export function updateFlightFilter(filter: App.FlightFilter): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const newFilters = [
      ...state.flights.filters.filter(f => f.id !== filter.id),
      filter,
    ]

    dispatch({
      type: UPDATE_FLIGHT_FILTERS,
      data: newFilters,
    })
  }
}

export function updateFlightsSort(sortValue: FlightSortOptions): AppAction {
  return {
    type: UPDATE_FLIGHTS_SORT,
    data: sortValue,
  }
}

export function updateFlightReservation(
  reservationId: string,
  flightItemId: string,
  payload: {
    contactInfo: {
      email: string
      phone: string
    }
    emergencyContact?: App.FlightEmergencyContact
  },
): AppAction {
  return (dispatch, _getState) => {
    dispatch({
      type: API_CALL,
      api: UPDATE_FLIGHT_RESERVATION_DETAILS,
      id: flightItemId,
      request: () => updateFlightReservationDetails(reservationId, payload),
    })
  }
}

export function updateFlightTravellers(
  reservationId: string,
  travellers: Array<App.FlightTraveller>,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.isUpdatingTravellers) {
      return
    }

    dispatch({
      type: API_CALL,
      api: UPDATE_FLIGHT_TRAVELLERS,
      request: () => updateFlightReservationTravellers(reservationId, travellers),
    })
  }
}

export function clearFlightsResult(): AppAction {
  return {
    type: CLEAR_FLIGHT_RESULT,
  }
}

export function clearFlightSearchCache(): AppAction {
  return {
    type: CLEAR_FLIGHT_SEARCH_CACHE,
  }
}

export function resetFlightsFilters(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const newFilters = state.flights.filters.map(f => resetFilter(f))

    dispatch({
      type: UPDATE_FLIGHT_FILTERS,
      data: newFilters,
    })
  }
}

export function fetchFlightPrice(
  destinationAirportCode: string,
  rooms: Array<App.Occupants>,
  startDate: string,
  endDate: string,
  flightOrigin?: string,
  nights?: number,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const finalOrigin = flightOrigin || getDefaultAirportCode(state)

    const requestProps: FlightPriceKey = {
      startDate,
      endDate,
      numberOfAdults: sum(rooms, (room) => room.adults),
      numberOfChildren: sum(rooms, (room) => room.children ?? 0),
      numberOfInfants: sum(rooms, (room) => room.infants ?? 0),
      origin: finalOrigin,
      destination: destinationAirportCode,
      nights: nights ?? 0,
    }

    const currency = state.geo.currentCurrency
    const region = state.geo.currentRegionCode
    const key = getFlightPriceKey(requestProps)

    if (
      // can be 0, which is still falsey - but we don't want to refetch it
      typeof state.flights.flightPrices[key] !== 'undefined' ||
      state.flights.flightPricesLoading[key]
    ) {
      // fetching or already fetched, skip
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_PRICE,
      request: () => getFlightPrice({ ...requestProps, currency, region }),
      key,
    })
  }
}

export function fetchFlightPrices(
  destinationAirportsCodes: Array<string>,
  rooms: Array<App.Occupants>,
  startDate: string,
  endDate: string,
  flightOrigin?: string,
  nights?: number,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const finalOrigin = flightOrigin || getDefaultAirportCode(state)

    const requestProps = {
      startDate,
      endDate,
      numberOfAdults: sum(rooms, (room) => room.adults),
      numberOfChildren: sum(rooms, (room) => room.children ?? 0),
      numberOfInfants: sum(rooms, (room) => room.infants ?? 0),
      origin: finalOrigin,
      nights,
    }

    const currency = state.geo.currentCurrency
    const region = state.geo.currentRegionCode
    // dedupe airports first
    const missingDestination = Array.from(new Set(destinationAirportsCodes.filter(Boolean)))
      // now get our keys
      .map(destination => ({ key: getFlightPriceKey({ ...requestProps, destination }), airportCode: destination }))
      // finally find those that aren't already fetched/fetching
      .filter(destination =>
        // can be 0, which is still falsey - but we don't want to refetch it
        typeof state.flights.flightPrices[destination.key] === 'undefined' &&
        !state.flights.flightPricesLoading[destination.key],
      )

    missingDestination.forEach(destination => {
      dispatch({
        type: API_CALL,
        api: FETCH_FLIGHT_PRICE,
        request: () => getFlightPrice({ ...requestProps, destination: destination.airportCode, currency, region, nights }),
        key: destination.key,
      })
    })
  }
}

export function fetchPopularAirports(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.popularAirports.origins.length || state.flights.popularAirports.fetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_POPULAR_AIRPORTS,
      request: () => getPopularAirportsByRegion(state.geo.currentRegionCode),
    })
  }
}

export function setFlightCreditData(creditDetails: FlightsCreditDetails): AppAction {
  return {
    type: SET_FLIGHTS_CREDIT_DATA,
    data: creditDetails,
  }
}

export function fetchFlightDeals(dealCategory: string = 'all'): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const finalCategory = dealCategory === 'all' ? undefined : dealCategory

    const existingList = state.flights.flightDealLists[dealCategory]
    if (existingList) {
      // if a list exists, it must've been initialised and is currently fetching/has fetched or is errored
      // we don't need to do it again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_DEALS,
      request: () => getFlightDealsByRegion(state.geo.currentRegionCode, finalCategory),
      // only thing we key on at the moment is deal category
      key: dealCategory,
    })
  }
}

export function fetchFlightDeal(id: string, token?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.flightDeals[id]) {
      // already done (or doing), don't do it again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_DEAL,
      request: () => getFlightDeal(id, token),
      id,
    })
  }
}

interface FlightCalendarProps {
  startDate: string
  endDate: string
  origin: string
  destination: string
  nights?: number
  tripType?: App.FlightCalendarTripType
  fareClass?: FlightsClassTypes
}

const flightCalendarFareClasses: Array<App.FlightsFareClass> = ['economy', 'premium', 'business', 'first']

function isFlightCalendarFareClass(fareClass?: string): fareClass is App.FlightsFareClass {
  return typeof fareClass === 'string' && flightCalendarFareClasses.includes(fareClass as App.FlightsFareClass)
}

export function fetchFlightCalendar(props: FlightCalendarProps): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode
    const currency = state.geo.currentCurrency

    const filterKey = getFlightCalendarKey({ ...props, region, currency })
    if (state.flights.flightCalendar[filterKey] || state.flights.flightCalendarLoading[filterKey]) {
      // already have fetched the calendar for the current filters
      return
    }

    if (props.fareClass && !isFlightCalendarFareClass(props.fareClass)) {
      return
    }

    const requestData = {
      startDate: props.startDate,
      endDate: props.endDate,
      origin: props.origin,
      destination: props.destination,
      numberOfNights: props.tripType === 'OneWay' ? 0 : (props.nights || 1),
      tripType: props.tripType,
      fareClass: props.fareClass,
      region,
      currency,
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_CALENDAR,
      request: () => getFlightCachePrices(requestData),
      key: filterKey,
    })
  }
}
interface FetchFlightUpsellFaresParams {
  searchId: string
  sessionId: string
  origin: string
  destination: string
  occupancies: App.Occupants
  journeyId: string
  departingJourneyId?: string
  departingFareFamilyId?: string
  isStandalone?: boolean
  isBundledFlow?: boolean
  feederOriginAirportCode?: string
}

export function fetchFlightFareFamilies(params: FetchFlightUpsellFaresParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const standaloneLuxPlusPricingEnabled = !!(params.isStandalone && getIsEnabledFlightsLuxPlusPricing(state))
    const bundledLuxPlusPricingEnabled = !!(params.isBundledFlow && getIsEnabledFlightsBundledLuxPlusPricing(state))
    const canRequestLuxPlusPricing = getCanViewLuxPlusBenefits(state) || (!!params.isBundledFlow && isLuxPlusSubscriptionInCart(state))
    const requestLuxPlusPricing = !!((standaloneLuxPlusPricingEnabled || bundledLuxPlusPricingEnabled) && canRequestLuxPlusPricing)
    const selectedFareIds: Array<string> = []
    if (params.departingJourneyId) {
      selectedFareIds.push(params.departingJourneyId)
    }
    selectedFareIds.push(params.journeyId)
    const requestProps = {
      ...params,
      departingUpsellOptionId: params.departingFareFamilyId,
      selectedFareIds,
      region: state.geo.currentRegionCode,
      email: state.auth.account.email,
      requestLuxPlusPricing,
    }

    const key = getObjectKey({
      searchId: params.searchId,
      origin: params.origin,
      destination: params.destination,
      fareId: params.journeyId,
      departingFareId: params.departingJourneyId,
      departingUpsellOptionId: params.departingFareFamilyId,
      feederOriginAirportCode: params.feederOriginAirportCode,
    })

    const stateFareFamilies = state.flights.fareFamiliesByFlight[key]

    // already fetching or have already fetched without error
    if (stateFareFamilies && !stateFareFamilies.error) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_FARE_FAMILIES,
      request: () => getFlightFareFamilies(requestProps).then(({ upsellFares, upsellFaresPerLeg }) => {
        if (upsellFares.length === 0) {
          throw new Error('No fare families found for flight')
        }

        return { upsellFares, upsellFaresPerLeg }
      }),
      key,
    })
  }
}

export function fetchFlightCampaigns(region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.campaigns[region]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_CAMPAIGNS,
      request: () => getFlightCampaigns(region),
      key: region,
    })
  }
}

export function fetchFlightCampaign(id: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.campaign[id]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_CAMPAIGN,
      request: () => getFlightCampaign(id),
      key: id,
    })
  }
}

export function fetchFlightCampaignDeals(id: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.campaignDeals[id]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_CAMPAIGN_DEALS,
      request: () => getFlightCampaignDeals(id),
      key: id,
    })
  }
}

export function fetchRecentFlightSearches(): AppAction {
  return {
    type: API_CALL,
    api: FETCH_RECENT_FLIGHT_SEARCHES,
    request: () => getRecentFlightSearches(),
  }
}

export function fetchFlightsDateChangeFlightsList(id: string, selectedLegs: Array<App.LegWithFlightItemIdWithNewDepartureDate>, key: string, skipCache: boolean = false): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.flightsDateChangeSearch[key] && !skipCache) {
      // already fetched/are fetching, don't try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHTS_DATE_CHANGE_FLIGHTS_LIST,
      request: () => getFlightsDateChangeFlightsList(id, state.geo.currentRegionCode, selectedLegs),
      key,
    })
  }
}

export function fetchFlightSeats(searchId: string, fareIds: Array<string>, travelers: Array<FlightSeatTraveler>, key: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.seatsMap?.[key]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_SEATS,
      key,
      request: () => getFlightSeats({
        searchId,
        currency: state.geo.currentCurrency,
        region: state.geo.currentRegionCode,
        isStandalone: true,
        brand: config.BRAND,
        selectedFareIds: fareIds,
        travelers,
      }),
    })
  }
}

export function setFlightDateChangeStep(step: FlightChangeDatesPageStepDefinition) {
  return {
    type: FLIGHTS_DATE_CHANGE_SET_STEP,
    step,
  }
}

export function setFlightDateChangeSelectedLegsToChange(selectedLegs: Array<App.LegWithFlightItemId>) {
  return {
    type: FLIGHTS_DATE_CHANGE_SELECTED_LEGS_TO_CHANGE,
    selectedLegsToChange: selectedLegs,
  }
}

export function setFlightDateChangeSelectedLegsWithDates(selectedLegsWithDates: Array<App.LegWithFlightItemIdWithNewDepartureDate>) {
  return {
    type: FLIGHTS_DATE_CHANGE_NEW_LEGS_WITH_DATES,
    selectedLegsWithDates,
  }
}

export function resetFlightDateChange() {
  return {
    type: FLIGHTS_DATE_CHANGE_RESET,
  }
}

export function resetFlightItemsFromCheckout() {
  return {
    type: CHECKOUT_RESET_FLIGHT_ITEMS,
  }
}

export function updateFlightSeatSelections(
  key: string,
  seatSelections: SeatSelections,
): AppAction {
  return (dispatch) => {
    dispatch({
      type: UPDATE_FLIGHT_SEAT_SELECTIONS,
      key,
      seatSelections,
    })

    dispatch({
      type: CHECKOUT_UPDATE_FLIGHT_SEAT_SELECTIONS,
      key,
      seatSelections,
    })
  }
}

export function clearFlightSeatSelections(): AppAction {
  return (dispatch) => {
    dispatch({
      type: CLEAR_FLIGHT_SEAT_SELECTIONS,
    })

    dispatch({
      type: CHECKOUT_CLEAR_FLIGHT_SEAT_SELECTIONS,
    })
  }
}

export function updateEconomyLuxCouplesConfiguration(
  configuration: App.EconomyLuxCouplesConfiguration,
): AppAction {
  return (dispatch) => {
    dispatch({
      type: CHECKOUT_UPDATE_ECONOMY_LUX_COUPLES_CONFIGURATION,
      configuration,
    })
  }
}

export function clearEconomyLuxCouplesConfiguration(): AppAction {
  return (dispatch) => {
    dispatch({
      type: CHECKOUT_CLEAR_ECONOMY_LUX_COUPLES_CONFIGURATION,
    })
  }
}

export function clearFlightSeatSelectionsFromRedis(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const flightItems = getFlightItems(state)

    flightItems.forEach((item, index) => {
      if (item.hasFinalFare) {
        dispatch(getCheckoutFlightItemFinalFare(item, index))
      }
    })
  }
}

export function fetchFlightRefundDetails(reservationId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.flights.penalties?.[reservationId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_PENALTIES_DETAILS,
      key: reservationId,
      request: () => getFlightPenaltiesDetails(reservationId),
    })
  }
}

interface FlightDateGridProps {
  departureDate: string
  returnDate?: string
  origin: string
  destination: string
  fareType: string
  carrier?: string
  occupants: App.Occupants
  fareClass?: string
  isNDC?: boolean
}

export function fetchFlightDateGrid(props: FlightDateGridProps, skipCache: boolean = false): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode
    const isLuxPlus = getIsEnabledFlightsLuxPlusPricing(state) && getCanViewLuxPlusBenefits(state)

    const cacheKey = getFlightDateGridKey({
      departureDate: props.departureDate,
      returnDate: props.returnDate,
      origin: props.origin,
      destination: props.destination,
      fareType: props.fareType,
      occupants: props.occupants,
      fareClass: props.fareClass,
    })

    if ((state.flights.flightDateGrid?.[cacheKey] || state.flights.flightDateGridLoading?.[cacheKey]) && !skipCache) {
      return
    }

    const requestData: FlightDateGridRequest = {
      departureDate: props.departureDate,
      returnDate: props.returnDate,
      alternativeDatesCount: props.isNDC ? FLIGHT_DATE_GRID_ALTERNATIVE_DATES_COUNT_NDC : FLIGHT_DATE_GRID_ALTERNATIVE_DATES_COUNT,
      origin: props.origin,
      destination: props.destination,
      passengers: {
        adults: props.occupants.adults,
        children: props.occupants.children,
        infants: props.occupants.infants,
      },
      region,
      carrier: props.carrier,
      fareClass: props.fareClass,
      isLuxPlus,
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_DATE_GRID,
      key: cacheKey,
      request: () => getFlightDateGrid(requestData),
    })
  }
}

export function createFareWatch(payload: CreateFareWatchRequest): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const userId = state.auth.account.memberId

    if (!userId) {
      throw new Error('No user ID')
    }

    const fareWatch = await createFareWatchRequest(userId, payload)

    const key = getFareWatchStatusKey({
      tripType: payload.tripType,
      fareClass: payload.fareClass,
      itineraries: payload.itineraries,
    })

    dispatch({
      type: API_CALL_SUCCESS,
      api: FETCH_FARE_WATCH_STATUS,
      key,
      data: { active: true, id: fareWatch.id },
    })
  }
}

export function fetchFareWatches(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const userId = state.auth.account.memberId

    if (!userId) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FARE_WATCHES,
      request: () => fetchFareWatchesRequest(userId),
    })
  }
}

export function deleteFareWatch(fareWatchId: string): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const userId = state.auth.account.memberId

    if (!userId) {
      throw new Error('No user ID')
    }

    await deleteFareWatchRequest(fareWatchId, userId)
    dispatch({ type: API_CALL_SUCCESS, api: DELETE_FARE_WATCH, fareWatchId, data: undefined })
  }
}

export function fetchFareWatchStatus(query: FareWatchStatusQuery): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const userId = state.auth.account.memberId

    if (!userId) {
      return
    }

    const key = getFareWatchStatusKey(query)

    if (state.flights.searchFareWatchMap?.[key]?.fetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FARE_WATCH_STATUS,
      key,
      request: () => fetchFareWatchStatusRequest(userId, query),
    })
  }
}

export function fetchFareSenseSuggestions(searchId: string, offerId?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const userId = state.auth.account.memberId

    if (!userId || !searchId) {
      return
    }

    const key = getObjectKey({ searchId, offerId })
    const existing = state.flights.fareSenseSuggestions?.[key]
    if (existing?.fetching || existing?.data) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FARESENSE_SUGGESTIONS,
      key,
      request: () => getFareSenseSuggestions(searchId, offerId),
    })
  }
}
